import type { Incentive, ProgramIncentives } from '@makemydeal/dr-dash-types';
import { INCENTIVE_TYPE_VALUES, NON_MANUAL_INCENTIVE_DISPLAY_TYPES } from '@makemydeal/dr-dash-types';
import { roundToTwoDecimal } from '@makemydeal/dr-common-utils';
import type { IResponsePayment } from '@makemydeal/dr-platform-types';
import { mapPaymentResponseToProgramQuote } from '@makemydeal/dr-dash-store/utils/programUtils';
import { filterActiveIncentives } from '@makemydeal/dr-dash-store/utils/incentivesUtil';
import { isApplicableManualIncentive } from '@makemydeal/dr-dash-store/utils/firstPencilUtils';
import { getIncentivesWithDeterminedAmountsByTerm } from '@makemydeal/dr-dash-store/selectors/programsSelectors';

import { createProgramTableData, isProgramNotExpired } from '../Programs/utils';
import { CREDIT_TIER_OPTIONS } from '../Programs/creditTiers';
import type { CreditTierKey } from '../Programs/creditTiers';
import type {
    FirstPencilIncentiveOverride,
    FirstPencilIncentiveSelection,
    FirstPencilManualIncentive,
    FirstPencilProgramQuoteRequest
} from '@makemydeal/dr-dash-store/types/firstPencilTypes';
import type { FirstPencilIncentiveRow, FirstPencilProgramRow, FirstPencilProgramsOfferType } from './types';

export const FIRST_PENCIL_PROGRAM_TABS: { value: FirstPencilProgramsOfferType; label: string }[] = [
    { value: 'finance', label: 'Finance' },
    { value: 'lease', label: 'Lease' }
];

export const DEFAULT_FIRST_PENCIL_PROGRAMS_TAB: FirstPencilProgramsOfferType = 'finance';

export const mapQuotesToProgramRows = (quotes: readonly IResponsePayment[]): FirstPencilProgramRow[] => {
    const programs = quotes
        .map((quote) => {
            const { compatibleIncentiveIds, creditScoreOverride } = quote as IResponsePayment & {
                compatibleIncentiveIds?: number[];
                creditScoreOverride?: number;
            };
            return { ...mapPaymentResponseToProgramQuote(quote), compatibleIncentiveIds, creditScoreOverride };
        })
        .filter(isProgramNotExpired);
    return createProgramTableData(programs, true) as FirstPencilProgramRow[];
};

export const buildAllTiersRequests = (currentTier: CreditTierKey | undefined): FirstPencilProgramQuoteRequest[] => {
    const primaryOption = CREDIT_TIER_OPTIONS.find((option) => option.value === currentTier) ?? CREDIT_TIER_OPTIONS[0];
    const orderedOptions = [primaryOption, ...CREDIT_TIER_OPTIONS.filter((option) => option.value !== primaryOption.value)];
    return orderedOptions.map((option) => ({
        creditScoreOverride: option.score,
        requestedTier: option.value,
        primary: option.value === primaryOption.value
    }));
};

const toIncentiveRow = (
    incentive: Incentive,
    displayType: string,
    selected: boolean,
    isConditional: boolean
): FirstPencilIncentiveRow => ({
    key: incentive.incentiveId,
    incentiveId: incentive.incentiveId,
    programId: incentive.programId,
    programName: incentive.programName,
    description: incentive.description,
    incentiveType: incentive.incentiveType,
    displayType,
    cash: incentive.cash,
    selected,
    isConditional,
    exclusions: incentive.exclusions ?? []
});

export const buildProgramIncentiveRows = (
    program: FirstPencilProgramRow | undefined,
    incentives: Partial<ProgramIncentives> | undefined,
    offerType: FirstPencilProgramsOfferType,
    isRestorationEnabled: boolean,
    selection?: FirstPencilIncentiveSelection
): FirstPencilIncentiveRow[] => {
    if (!program || !incentives) {
        return [];
    }
    const term = program.term as number;
    const determine = (list: Incentive[] | undefined) =>
        filterActiveIncentives(getIncentivesWithDeterminedAmountsByTerm(list ?? [], term), offerType);
    const appliedIds = new Set(program.appliedIncentiveIds ?? []);
    const isSelected = (incentiveId: number, selectedIds: number[] | undefined) =>
        selectedIds ? selectedIds.includes(incentiveId) : appliedIds.has(incentiveId);
    const compatibleIds = program.compatibleIncentiveIds;
    const isCompatible = (incentive: Incentive) =>
        !compatibleIds || compatibleIds.length === 0 || compatibleIds.includes(incentive.incentiveId);

    const nonConditionalRows = determine(incentives.nonConditional).map((incentive) =>
        toIncentiveRow(
            incentive,
            NON_MANUAL_INCENTIVE_DISPLAY_TYPES.manufacturer,
            isSelected(incentive.incentiveId, selection?.nonConditionalIds),
            false
        )
    );
    const conditionalRows = determine(incentives.conditional)
        .filter(isCompatible)
        .map((incentive) =>
            toIncentiveRow(
                incentive,
                incentive.isStandardIncentive
                    ? NON_MANUAL_INCENTIVE_DISPLAY_TYPES.manufacturer
                    : isRestorationEnabled
                    ? NON_MANUAL_INCENTIVE_DISPLAY_TYPES.conditional
                    : NON_MANUAL_INCENTIVE_DISPLAY_TYPES.included,
                isSelected(incentive.incentiveId, selection?.conditionalIds),
                true
            )
        );
    return [...nonConditionalRows, ...conditionalRows];
};

export const findBlockingIncentive = (
    row: FirstPencilIncentiveRow,
    rows: FirstPencilIncentiveRow[]
): FirstPencilIncentiveRow | undefined =>
    row.selected ? undefined : rows.find((other) => other.selected && other.exclusions.includes(row.incentiveId));

export const toIncentiveSelection = (rows: FirstPencilIncentiveRow[]): FirstPencilIncentiveSelection => {
    const selectedIds = (isConditional: boolean) =>
        rows.filter((row) => row.isConditional === isConditional && row.selected).map((row) => row.incentiveId);
    return { conditionalIds: selectedIds(true), nonConditionalIds: selectedIds(false) };
};

export const toggleIncentiveSelection = (
    rows: FirstPencilIncentiveRow[],
    toggled: FirstPencilIncentiveRow,
    checked: boolean
): FirstPencilIncentiveSelection =>
    toIncentiveSelection(rows.map((row) => (row.incentiveId === toggled.incentiveId ? { ...row, selected: checked } : row)));

export const toManualIncentiveCode = (value: string): number | undefined => {
    const code = Number(value.replace(/\D/g, ''));
    return code > 0 ? code : undefined;
};

export const toManualIncentiveAmount = (value: string): number => {
    const amount = Number(value.replace(/[^0-9.]/g, ''));
    return Number.isFinite(amount) ? amount : 0;
};

export const hasDuplicateManualIncentiveCode = (
    incentive: { editId: string; code?: number },
    manualIncentives: { editId: string; code?: number; deleted: boolean }[]
): boolean =>
    incentive.code != null &&
    manualIncentives.some((other) => other.editId !== incentive.editId && !other.deleted && other.code === incentive.code);

export type FirstPencilIncentiveTotals = {
    dealerCashTotal: number;
    incentivesTotal: number;
};

export const withIncentiveOverrideAmounts = (
    rows: FirstPencilIncentiveRow[],
    overrides: Record<number, FirstPencilIncentiveOverride> | undefined
): FirstPencilIncentiveRow[] =>
    rows.map((row) => {
        const override = overrides?.[row.incentiveId];
        return override ? { ...row, cash: override.amount } : row;
    });

export const getFirstPencilIncentiveTotals = (
    incentiveRows: FirstPencilIncentiveRow[],
    manualIncentives: FirstPencilManualIncentive[]
): FirstPencilIncentiveTotals => {
    const amounts = [
        ...incentiveRows.filter((row) => row.selected).map((row) => ({ type: row.incentiveType, amount: row.cash })),
        ...manualIncentives.filter(isApplicableManualIncentive).map(({ type, amount }) => ({ type, amount }))
    ];
    const sum = (isDealerCash: boolean) =>
        roundToTwoDecimal(
            amounts
                .filter(({ type }) => (type === INCENTIVE_TYPE_VALUES.dealerCash.value) === isDealerCash)
                .reduce((total, { amount }) => total + (amount || 0), 0)
        );
    return { dealerCashTotal: sum(true), incentivesTotal: sum(false) };
};
